import { MarketArchetypeEventType, MarketArchetypeUnacceptedEventType } from '../../../api/markets'

/**
 * Event types on a market archetype.
 *
 * There is no `allowedEventTypeIds` in any response. `eventTypes` holds the accepted types and
 * `unacceptedEventTypes` the types its events use outside that list. Only `eventTypes` may be sent
 * back as `allowedEventTypeIds`; adding the unaccepted ones would silently accept the mismatch.
 *
 * Nothing is enforced on save. An empty accepted list means nothing has been declared yet.
 */
export const acceptedEventTypeIds = (eventTypes?: MarketArchetypeEventType[]): string[] => (eventTypes || []).map((eventType) => eventType.typeId)

/**
 * Names of the accepted types, in the order the endpoint returns them (most used first), for the
 * count's hover in the blueprint list.
 */
export const acceptedEventTypeNames = (eventTypes: MarketArchetypeEventType[] | undefined, nameOf: (typeId: string) => string): string[] =>
  (eventTypes || []).map((eventType) => nameOf(eventType.typeId) || eventType.typeId)

/**
 * Active event count per event type, accepted or not, for captioning the types. A type with no entry
 * has no active events, the same as one reporting zero.
 */
export const eventTypeUsage = (marketArchetype?: {
  eventTypes?: MarketArchetypeEventType[]
  unacceptedEventTypes?: MarketArchetypeUnacceptedEventType[]
}): { [typeId: string]: number } => {
  const usage: { [typeId: string]: number } = {}
  marketArchetype?.eventTypes?.forEach((eventType) => {
    usage[eventType.typeId] = eventType.count
  })
  marketArchetype?.unacceptedEventTypes?.forEach((eventType) => {
    usage[eventType.typeId] = eventType.events.length
  })
  return usage
}

/**
 * The accepted types being dropped from the list that still have active events of their own.
 *
 * Saving is never blocked over these — the BE stores the list without enforcing it, and existing
 * events keep their type. Those events simply start reporting as mismatches, which is worth knowing
 * before the list is narrowed.
 */
export const droppedEventTypesWithEvents = (selectedEventTypeIds: string[], eventTypes?: MarketArchetypeEventType[]): MarketArchetypeEventType[] =>
  (eventTypes || []).filter((eventType) => eventType.count > 0 && !selectedEventTypeIds.includes(eventType.typeId))

/**
 * Whether a game blueprint accepts an event of this type.
 *
 * Nothing on the backend blocks a mismatch, so this only drives the warning in the event editor. An
 * archetype that has declared nothing accepts anything, as does a blueprint with no archetype.
 */
export const gameBlueprintSelectableForEventType = (
  archetype: { marketArchetypeId?: string },
  eventTypeId: string | undefined,
  marketArchetypes?: { id: string; eventTypes?: MarketArchetypeEventType[] }[]
): boolean => {
  if (!eventTypeId || !archetype.marketArchetypeId) return true
  const marketArchetype = marketArchetypes?.find((candidate) => candidate.id === archetype.marketArchetypeId)
  const accepted = marketArchetype?.eventTypes || []
  if (accepted.length === 0) return true
  return accepted.some((eventType) => eventType.typeId === eventTypeId)
}

/**
 * The event's accepted-odd-case mark once its type is changed. The mark accepts one type, so a
 * retyped event is decided on again.
 */
export const typeAcceptedAfterTypeChange = (typeAccepted: boolean | undefined, previousTypeId: string, nextTypeId: string): boolean =>
  nextTypeId === previousTypeId ? !!typeAccepted : false

/**
 * Whether saving has to wait for the data a mismatch check reads — the Market Level Blueprints, the
 * game's blueprints. Without it every blueprint would read as accepting the event's type, so the
 * warning would be skipped. Nothing on a blueprint has nothing to check, and a failed load does not
 * hold the analyst up.
 */
export const saveWaitsForMismatchData = (gameBlueprintId: string | undefined, sources: { data?: unknown; isError?: boolean }[]): boolean =>
  !!gameBlueprintId && sources.some((source) => source.data === undefined && !source.isError)
